import {
    AuditLogChangeType,
    AuditLogDiff,
    HistoryDetailsItem,
    ResourceType,
} from "@common/types/auditLog";
import { HistoryField } from "@common/constants/history-source-types";
import {
    ACCOUNT_ROLE_COPY,
    isAccountRole,
} from "@common/constants/domains/accounting/accountRole";
import {
    getForm1099BoxLabel,
    isForm1099Box,
} from "@common/constants/domains/accounting/form1099Box";
import { currencyFormatter } from "@common/helpers/currency";
import { displayAccountLabel } from "@common/helpers/accounting";
import { z } from "zod";
import {
    formatAuditLogRefValue,
    getPreferredSnapshot,
} from "../../utils/historyDetailsHelpers";

export interface CoaSubject {
    name: string;
    code: string | null;
    groupPath: string[];
}

export type HistoryAccountValue =
    | { kind: "text"; text: string }
    | { kind: "groupPath"; segments: string[] }
    | { kind: "notApplicable" };

export const EMPTY_TEXT = "-";
export const NOT_APPLICABLE_VALUE: HistoryAccountValue = {
    kind: "notApplicable",
};
export const EMPTY_VALUE: HistoryAccountValue = {
    kind: "text",
    text: EMPTY_TEXT,
};

export const toTextValue = (text: string): HistoryAccountValue => ({
    kind: "text",
    text,
});

const NonEmptyStringSchema = z.string().min(1);

const CoaSnapshotSchema = z
    .object({
        [HistoryField.NAME]: NonEmptyStringSchema,
        [HistoryField.CODE]: NonEmptyStringSchema.nullish().catch(null),
        [HistoryField.TYPE]: z.string().nullish().catch(null),
        [HistoryField.PARENT_GROUP]: z.array(z.string()).catch([]),
    })
    .transform((snapshot): CoaSubject => ({
        name: snapshot.name,
        code: snapshot.code ?? null,
        groupPath:
            typeof snapshot.type === "string"
                ? [snapshot.type, ...snapshot.parentGroup]
                : snapshot.parentGroup,
    }));

const BalanceSchema = z
    .object({ balance: z.number() })
    .transform(({ balance }) => balance)
    .nullish()
    .catch(null);

const RemovedOpeningBalanceSchema = z.object({
    main: BalanceSchema,
    adjustment: BalanceSchema,
});

interface ParseMetadataValueParams<Schema extends z.ZodTypeAny> {
    log: HistoryDetailsItem;
    key: string;
    schema: Schema;
}

function parseMetadataValue<Schema extends z.ZodTypeAny>({
    log,
    key,
    schema,
}: ParseMetadataValueParams<Schema>): z.output<Schema> | undefined {
    const result = schema.safeParse(log.metadata?.[key]);
    return result.success ? result.data : undefined;
}

export function readPreferredCoaSubject(
    log: HistoryDetailsItem,
): CoaSubject | undefined {
    const result = CoaSnapshotSchema.safeParse(getPreferredSnapshot(log));
    return result.success ? result.data : undefined;
}

function getNameFromDiff(log: HistoryDetailsItem): string | undefined {
    const nameDiff = log.diff.find((diff) => diff.field === HistoryField.NAME);
    if (!nameDiff) {
        return undefined;
    }
    const orderedRefs =
        log.changeType === AuditLogChangeType.DELETE
            ? [nameDiff.before, nameDiff.after]
            : [nameDiff.after, nameDiff.before];
    return orderedRefs
        .map(formatAuditLogRefValue)
        .find((value) => value !== "-");
}

function resolveFallbackName(log: HistoryDetailsItem): string {
    const resourceName = log.resource?.name;
    if (typeof resourceName === "string" && resourceName.length > 0) {
        return resourceName;
    }
    return (
        getNameFromDiff(log) ??
        (log.resourceType === ResourceType.ACCOUNT_GROUP
            ? "Deleted account group"
            : "Deleted account")
    );
}

export function resolveCoaSubject(log: HistoryDetailsItem): CoaSubject {
    return (
        readPreferredCoaSubject(log) ?? {
            name: resolveFallbackName(log),
            code: null,
            groupPath: [],
        }
    );
}

export type IdentifierFormatter = (
    subject: Pick<CoaSubject, "name" | "code">,
) => string;

export const formatNameIdentifier: IdentifierFormatter = ({ name }) => name;

export const formatCodeAndNameIdentifier: IdentifierFormatter = ({
    name,
    code,
}) => displayAccountLabel({ codeText: code, name }, true);

const UNRESOLVED_REFERENCE_LABELS: Partial<Record<string, string>> = {
    [HistoryField.PARENT_GROUP_ID]: "Unknown group",
    [HistoryField.RELATED_ENTITY_ID]: "Unknown business",
};

export function formatAccountFieldValue(
    field: string,
    ref: AuditLogDiff["before"] | AuditLogDiff["after"],
): string {
    if (field === HistoryField.ROLE) {
        if (ref.value === null || ref.value === undefined) {
            return "None";
        }
        if (typeof ref.value === "string" && isAccountRole(ref.value)) {
            return ACCOUNT_ROLE_COPY[ref.value].label;
        }
    }
    if (field === HistoryField.FORM_1099_BOX) {
        if (ref.value === null || ref.value === undefined) {
            return "None";
        }
        if (typeof ref.value === "string" && isForm1099Box(ref.value)) {
            return getForm1099BoxLabel(ref.value) ?? ref.value;
        }
    }
    if (field === HistoryField.ARCHIVED && typeof ref.value === "boolean") {
        return ref.value ? "Archived" : "Active";
    }
    const unresolvedReferenceLabel = UNRESOLVED_REFERENCE_LABELS[field];
    if (unresolvedReferenceLabel !== undefined) {
        if (ref.value === null || ref.value === undefined) {
            return "None";
        }
        return ref.valueLabel ?? unresolvedReferenceLabel;
    }
    return formatAuditLogRefValue(ref);
}

function formatRemovedOpeningBalance(
    log: HistoryDetailsItem,
    diff: AuditLogDiff,
): string {
    const removed = parseMetadataValue({
        log,
        key: "removedOpeningBalance",
        schema: RemovedOpeningBalanceSchema,
    });
    const mainFromDiff =
        typeof diff.before.value === "number" ? diff.before.value : null;
    const main = removed ? (removed.main ?? null) : mainFromDiff;
    const adjustment = removed?.adjustment ?? null;
    const format = (amount: number): string => currencyFormatter.format(amount);
    if (adjustment === null) {
        return main === null ? "-" : format(main);
    }
    const signedAdjustment =
        adjustment < 0 ? format(adjustment) : `+${format(adjustment)}`;
    return main === null
        ? `${signedAdjustment} adjustment`
        : `${format(main)} (${signedAdjustment} adjustment)`;
}

function readGroupPathValue(
    log: HistoryDetailsItem,
    snapshotKey: "beforeSnapshot" | "afterSnapshot",
): HistoryAccountValue | undefined {
    const subject = parseMetadataValue({
        log,
        key: snapshotKey,
        schema: CoaSnapshotSchema,
    });
    return subject
        ? { kind: "groupPath", segments: subject.groupPath }
        : undefined;
}

interface DiffValues {
    previous: HistoryAccountValue;
    modified: HistoryAccountValue;
}

export function formatDiffValues(
    log: HistoryDetailsItem,
    diff: AuditLogDiff,
): DiffValues {
    if (diff.field === HistoryField.OPENING_BALANCE) {
        return {
            previous: toTextValue(formatRemovedOpeningBalance(log, diff)),
            modified: EMPTY_VALUE,
        };
    }
    const previousText = toTextValue(
        formatAccountFieldValue(diff.field, diff.before),
    );
    const modifiedText = toTextValue(
        formatAccountFieldValue(diff.field, diff.after),
    );
    if (diff.field === HistoryField.PARENT_GROUP_ID) {
        return {
            previous: readGroupPathValue(log, "beforeSnapshot") ?? previousText,
            modified: readGroupPathValue(log, "afterSnapshot") ?? modifiedText,
        };
    }
    return { previous: previousText, modified: modifiedText };
}

export function readMergedIntoAccountId(
    log: HistoryDetailsItem,
): string | undefined {
    return parseMetadataValue({
        log,
        key: "mergedIntoAccountId",
        schema: NonEmptyStringSchema,
    });
}

export function readMergedIntoSubject(
    log: HistoryDetailsItem,
): CoaSubject | undefined {
    const snapshotSubject = parseMetadataValue({
        log,
        key: "mergedIntoAccountSnapshot",
        schema: CoaSnapshotSchema,
    });
    if (snapshotSubject) {
        return snapshotSubject;
    }
    const name = parseMetadataValue({
        log,
        key: "mergedIntoAccountName",
        schema: NonEmptyStringSchema,
    });
    return name ? { name, code: null, groupPath: [] } : undefined;
}
